import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Spayzo — We're here to help" },
      { name: "description", content: "Get in touch with the Spayzo team. Reach us by email, phone, or send a message — we usually reply within a few hours." },
      { property: "og:title", content: "Contact Spayzo" },
      { property: "og:description", content: "Reach the Spayzo team by email, phone or message form." },
    ],
    links: [{ rel: "canonical", href: "https://spayzo.lovable.app/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({"@context": "https://schema.org", "@type": "LocalBusiness", "name": "Spayzo", "url": "https://spayzo.lovable.app/contact", "email": "hello@spayzo.app", "telephone": "+1-415-555-0142", "address": {"@type": "PostalAddress", "streetAddress": "548 Market St", "addressLocality": "San Francisco", "addressRegion": "CA", "addressCountry": "US"}}),
      },
    ],
  }),
  component: ContactPage,
});

type FormValues = { name: string; email: string; message: string };
type FormErrors = Partial<Record<keyof FormValues, string>>;

function ContactPage() {
  const { t } = useTranslation();
  const [values, setValues] = useState<FormValues>({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const schema = z.object({
    name: z.string().trim().min(1, t("contact.errors.name")).max(100),
    email: z.string().trim().email(t("contact.errors.email")).max(255),
    message: z.string().trim().min(10, t("contact.errors.message")).max(1000),
  });

  const channels = [
    { icon: Mail, key: "email", value: "hello@spayzo.app", href: "mailto:hello@spayzo.app" },
    { icon: Phone, key: "phone", value: "+1 (415) 555-0142", href: "tel:+14155550142" },
    { icon: MapPin, key: "office", value: t("contact.channels.officeValue"), href: "https://maps.google.com/?q=548+Market+St,+San+Francisco,+CA" },
  ] as const;

  const update = (k: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(values);
    if (!parsed.success) {
      const next: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof FormErrors;
        if (!next[key]) next[key] = issue.message;
      }
      setErrors(next);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 900));
    setSubmitting(false);
    setSent(true);
    toast.success(t("contact.sentToast"));
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />

      <main className="flex-1">
        <section className="border-b border-border bg-surface">
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("contact.eyebrow")}</p>
            <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("contact.title")}</h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">{t("contact.subtitle")}</p>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-5 lg:gap-14">
            <div className="lg:col-span-3">
              <div className="rounded-3xl border border-border bg-card p-6 shadow-soft sm:p-10">
                {sent ? (
                  <div className="flex flex-col items-center py-10 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <CheckCircle2 className="h-7 w-7" />
                    </div>
                    <h2 className="mt-5 font-display text-2xl font-semibold">{t("contact.sent")}</h2>
                    <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                      {t("contact.sentBody", { name: values.name.split(" ")[0], email: values.email })}
                    </p>
                    <Button
                      onClick={() => {
                        setSent(false);
                        setValues({ name: "", email: "", message: "" });
                      }}
                      variant="outline"
                      className="mt-6 rounded-full"
                    >
                      {t("contact.sendAnother")}
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} noValidate className="space-y-5">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <MessageCircle className="h-4 w-4" />
                      {t("contact.sendMessage")}
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="name">{t("contact.name")}</Label>
                        <Input id="name" value={values.name} onChange={update("name")} placeholder={t("contact.namePlaceholder")} maxLength={100} aria-invalid={!!errors.name} className="mt-1.5" />
                        {errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
                      </div>
                      <div>
                        <Label htmlFor="email">{t("contact.email")}</Label>
                        <Input id="email" type="email" value={values.email} onChange={update("email")} placeholder={t("contact.emailPlaceholder")} maxLength={255} aria-invalid={!!errors.email} className="mt-1.5" />
                        {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message">{t("contact.message")}</Label>
                      <Textarea id="message" value={values.message} onChange={update("message")} placeholder={t("contact.messagePlaceholder")} rows={6} maxLength={1000} aria-invalid={!!errors.message} className="mt-1.5 resize-none" />
                      <div className="mt-1.5 flex items-center justify-between">
                        {errors.message ? (
                          <p className="text-xs text-destructive">{errors.message}</p>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t("contact.neverShare")}</span>
                        )}
                        <span className="text-xs tabular-nums text-muted-foreground">{values.message.length}/1000</span>
                      </div>
                    </div>

                    <Button type="submit" size="lg" disabled={submitting} className="w-full rounded-full sm:w-auto">
                      {submitting ? (
                        <>
                          <Loader2 className="me-2 h-4 w-4 animate-spin" />
                          {t("contact.sending")}
                        </>
                      ) : (
                        t("contact.send")
                      )}
                    </Button>

                    <p className="text-xs text-muted-foreground">
                      {t("contact.agree")}{" "}
                      <Link to="/privacy" className="underline-offset-4 hover:underline">{t("contact.privacy")}</Link>.
                    </p>
                  </form>
                )}
              </div>
            </div>

            <aside className="space-y-4 lg:col-span-2">
              {channels.map((c) => (
                <a
                  key={c.key}
                  href={c.href}
                  target={c.key === "office" ? "_blank" : undefined}
                  rel={c.key === "office" ? "noreferrer" : undefined}
                  className="group flex items-start gap-4 rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-soft"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t(`contact.channels.${c.key}`)}</div>
                    <div className="mt-0.5 truncate font-medium text-foreground">{c.value}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{t(`contact.channels.${c.key}Hint`)}</div>
                  </div>
                </a>
              ))}

              <div className="overflow-hidden rounded-2xl border border-border">
                <iframe
                  title={t("contact.mapTitle")}
                  src="https://www.openstreetmap.org/export/embed.html?bbox=-122.4044%2C37.7884%2C-122.3984%2C37.7914&layer=mapnik&marker=37.7899%2C-122.4014"
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
